import { AddCircleOutline } from '@mui/icons-material';
import { GridComponent, Group, Sort, Filter, Toolbar, ExcelExport, Reorder, Resize, ColumnChooser, Inject, FilterSettingsModel, Page, ExcelQueryCellInfoEventArgs, Column, Search } from '@syncfusion/ej2-react-grids';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { GridType } from '../../../Api/UsersMicroserviceClient';
import InitSfGridLocale from '../../../Localization/SfGridLanguageInitializer';
import TrashGridDialog from '../../Shared/Trash/TrashGridDialog';
import { TrashType } from '../../Shared/Trash/TrashType';
import { Box, Button, Card, Grid, Switch } from '@mui/material';
import { useUserLayoutContext } from '../../../Contexts/Providers/UserLayoutMenuProvider';
import { useApi } from '../../../ApiOld/Api/useApi';
import { DeleteWarehousesRequest, SetWarehouseIsActiveRequest, WarehouseListPageDTO, WarehouseVovClient } from '../../../ApiOld/Api/ApiServerVov';
import GridComponentStatefull from '../../Shared/Datagrid/GridComponentStatefull';
import { useWarehousesPermissionContext } from '../../../Contexts/Permissions/WarehousesPermissionContext';

InitSfGridLocale();
interface WarehousesListProps {
}

export default function WarehousesList(props: Readonly<WarehousesListProps>) {
    const { permissions, reloadPermissions } = useWarehousesPermissionContext();
    const [warehouses, setWarehouses] = useState<WarehouseListPageDTO[] | undefined>();
    const [isFavorite, setIsFavorite] = React.useState(false);
    const [trashOpen, setTrashOpen] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const { setBackMenuFunction, setMenuButtons } = useUserLayoutContext();
    const { authorizedApi } = useApi(WarehouseVovClient);
    const grid = useRef<GridComponent>(null);
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const params = useParams();

    useEffect(() => {
        setIsMounted(true);
        const favorite = localStorage.getItem('warehouses_list_is_favorite');
        setIsFavorite((favorite ?? '') === 'true');
    }, [setIsFavorite]);

    useEffect(() => {
        if (!permissions)
            reloadPermissions();
    }, [permissions, reloadPermissions]);

    useEffect(() => {
        setBackMenuFunction(undefined);
        setMenuButtons(<Button
            size='small'
            disabled={!permissions?.stockWarehousesCreate}
            sx={{ mr: 2 }}
            startIcon={<AddCircleOutline />}
            variant='contained' onClick={() => {
                navigate({ pathname: '/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehouseDetail' });
            }
            }>{t('Add')}</Button>);
    }, [permissions, params, setBackMenuFunction, setMenuButtons, navigate, t]);

    useEffect(() => {
        if (isMounted && authorizedApi && grid.current)
            refresh();
    }, [isMounted, authorizedApi, isFavorite, grid.current]);

    const refresh = () => {
        authorizedApi.getWarehousesListPage(isFavorite).then((r) => {
            setWarehouses(r ? r.warehousesListPage : []);
            if (grid.current) {
                grid.current.dataSource = r ? r.warehousesListPage ?? [] : [];
                grid.current.refresh();
            }
        });
    }

    let groupOptions = {
        showDropArea: true,
        captionTemplate: '<span class="groupItems" style="color:#FFC600"> ${key} - ${count} Úkolů</span>'
    };

    let filterOptions: FilterSettingsModel = {
        type: 'Excel',
        ignoreAccent: true
    };

    function toolbarClickExcel(props: any) {
        if (grid.current !== undefined && props.item.id.toString().endsWith('excelexport'))
            grid.current!.excelExport();
    }

    const excelQueryCellInfo = (args: ExcelQueryCellInfoEventArgs): void => {
        if ((args.column as Column).field === 'isActive') {
            if ((args.data as WarehouseListPageDTO).isActive) {
                args.value = t('yesL');
            }
            else {
                args.value = t('noL');
            }
        }
        else if ((args.column as Column).field === 'openActions') {
            args.value = (args.data as WarehouseListPageDTO).openActions?.map((e) => formatDateTime(e.datetime) + ' ' + getStateName(e.state ?? '')).join(', ') ?? '';
        }
    }

    function navigateToOpenAction(warehouseId: number, id: number, state: string) {
        switch (state) {
            case 'draft_buy':
                navigate('/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehousePurchase/' + warehouseId + '/' + id);
                break;
            case 'draft_block':
                navigate('/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehouseBlock/0/' + warehouseId + '/' + id);
                break;
            case 'draft_out':
                navigate('/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehouseOut/0/' + warehouseId + '/' + id);
                break;
            case 'draft_utility':
                navigate('/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehouseUtility/' + warehouseId + '/' + id);
                break;
            case 'draft_write_off':
                navigate('/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehouseWriteOff/' + warehouseId + '/' + id);
                break;
            case 'in_waiting_for_signature':
                navigate('/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehouseDetail/' + warehouseId);
                break;
            default:
                break;
        }
    };

    function cellTemplateActive(props: WarehouseListPageDTO) {
        return <Switch size='small' checked={props.isActive} onChange={(e, ch) => changeIsActive(e)} />
    }

    const changeIsActive = (args) => {
        if (permissions?.stockWarehousesEdit) {
            if (grid.current) {
                let rowDetails = grid.current.getRowInfo(args.currentTarget);
                if (rowDetails?.rowIndex !== undefined && rowDetails?.rowData) {
                    (rowDetails.rowData as WarehouseListPageDTO).isActive = args.target.checked;

                    grid.current.updateRow(rowDetails.rowIndex, rowDetails.rowData); //it helps to update the changes in datasource

                    grid.current.refresh();
                    authorizedApi.setWarehouseIsActive(new SetWarehouseIsActiveRequest({ id: (rowDetails.rowData as WarehouseListPageDTO).id, isActive: args.target.checked }));
                }
            }
        }
    }

    function navigateToDetail(data: WarehouseListPageDTO) {
        if (permissions?.stockWarehousesDetail)
            navigate(
                { pathname: '/' + params['lang'] + '/' + params['domain']! + '/userlayout/warehouseDetail/' + data.id!.toString(), },
                { state: { id: data.id, inputRecords: warehouses } }
            );
    }

    function GridTemplate(props: Readonly<WarehouseListPageDTO>) {
        const [hover, setHover] = useState(false);
        if (permissions?.stockWarehousesDetail)
            return (<Box style={{ cursor: 'pointer' }} onClick={() => {
                navigateToDetail(props);
            }} onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}>
                <a style={{ fontWeight: hover ? 'bold' : 'normal', cursor: 'pointer', textDecoration: 'underline' }}>{props.name!}</a>
            </Box>);
        else return <Box >{props.name!}</Box>
    }

    function GridOpenActionTemplate(props: Readonly<WarehouseListPageDTO>) {
        const [hoveredItem, setHoveredItem] = useState<number | undefined>(undefined);
        return (
            <Box sx={{
                overflowY: 'auto',
                height: props.openActions && props.openActions.length > 0 ? `${Math.min(30 + ((props.openActions.length - 1) * 17), 81)}px` : '30px',
                '&::-webkit-scrollbar': {
                    width: '6px',
                    height: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                }
            }}>
                {props.openActions && props.openActions.length > 0 ? (
                    <Grid container spacing={2}>
                        {props.openActions.map((openAction, index) => (
                            <Grid item xs={12} key={openAction.stockListId}>
                                <Box sx={{ marginTop: index === 0 ? '15px' : '2px', display: 'flex', justifyContent: 'space-between' }}>
                                    {permissions?.stockWarehousesEdit && <Box sx={{ cursor: 'pointer' }} onClick={() => {
                                        navigateToOpenAction(props.id!, openAction.stockListId ?? 0, openAction.state ?? '');
                                    }} onMouseEnter={() => setHoveredItem(openAction.stockListId)}
                                        onMouseLeave={() => setHoveredItem(undefined)}>
                                        <a style={{ fontWeight: hoveredItem === openAction.stockListId ? 'bold' : 'normal', cursor: 'pointer', textDecoration: 'underline' }}>
                                            {formatDateTime(openAction.datetime) + ' ' + getStateName(openAction.state ?? '')}
                                        </a>
                                    </Box>}
                                    {!permissions?.stockWarehousesEdit && <Box sx={{ marginTop: '10px' }}>{formatDateTime(openAction.datetime) + ' ' + getStateName(openAction.state ?? '')}</Box>}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Box></Box>
                )}
            </Box>
        );
    }

    const getStateName = (state: string) => {
        switch (state) {
            case 'draft_buy':
                return t('warehouses.purchase');
            case 'draft_block':
                return t('warehouses.reservation');
            case 'draft_out':
                return t('warehouses.disbursement');
            case 'in_waiting_for_signature':
                return t('warehouses.receiptWaitingForSignature');
            case 'draft_utility':
                return t('warehouses.consumption');
            case 'draft_write_off':
                return t('warehouses.writeOff');
            default:
                return 'Neznámy stav';
        }
    }

    const formatDateTime = (datetime) => {
        const date = new Date(datetime);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}`;
    };

    if (permissions && !permissions.stockWarehousesList)
        return <Box>{t('noViewingPermissions')}</Box>;
    else
        return <Box sx={{ maxWidth: '100vw' }}>
            <Card sx={{ height: '100%' }} elevation={3}>
                {permissions && <GridComponentStatefull<WarehouseListPageDTO>
                    height={'calc(100vh - 372px)'}
                    gridType={GridType.Warehouses}
                    canDelete={permissions ? permissions.stockWarehousesDelete ?? false : false}
                    onDelete={(e) => { authorizedApi.deleteWarehouses(new DeleteWarehousesRequest({ warehousesIds: e as number[] })).then((e) => { if (e) refresh(); }); }}
                    data={warehouses ?? []}
                    templateColumns={[
                        { field: 'name', template: GridTemplate },
                        { field: 'openActions', template: GridOpenActionTemplate },
                        { field: 'isActive', template: cellTemplateActive }
                    ]}
                    canAdd={permissions ? permissions.stockManufacturersCreate ?? false : false}
                    canChangeGroup canToolbarGrid canExcelExport canFavorite
                    isFavorite={isFavorite}
                    onFavorite={(e) => { localStorage.setItem('warehouses_list_is_favorite', e ? 'true' : 'false'); setIsFavorite(e); }}
                    id="grid-component-warehouses_list"
                    allowSorting={true}
                    enablePersistence={false}
                    allowResizing={true}
                    showColumnChooser={true}
                    allowExcelExport={true}
                    allowMultiSorting={true}
                    allowGrouping={false}
                    locale={i18n.language}
                    excelQueryCellInfo={excelQueryCellInfo}
                    recordClick={(e: any) => {
                        if (e.column.editType !== 'booleanedit' && e.column.field !== 'isActive') {
                            navigateToDetail(e.rowData as WarehouseListPageDTO);
                        }
                    }}
                    rowHeight={30}
                    allowReordering
                    filterSettings={filterOptions}
                    allowPaging={true}
                    style={{ backgroundColor: 'white', overflow: 'auto' }}
                    pageSettings={{ pageSize: 20, pageSizes: [10, 20, 30, 50, 100] }}
                    toolbarClick={toolbarClickExcel}
                    columnDataStateChange={(e) => { console.log(e); }}
                    selectionSettings={{ checkboxMode: 'ResetOnRowClick' }}
                    allowFiltering={true} groupSettings={groupOptions}
                    ref={grid}
                    searchLabel={t('warehouses.searchInWarehouses')}
                    columnsXX={[
                        { width: '50', minWidth: '50', headerText: '', type: 'checkbox', showInColumnChooser: false },
                        { field: 'id', width: '46', headerText: 'ID', textAlign: 'Right', visible: false, showInColumnChooser: false },
                        { field: 'name', template: GridTemplate, headerText: t('name'), width: '120', minWidth: '100', type: 'string' },
                        { field: 'buildingName', headerText: t('Projekt'), width: '120', minWidth: '100', type: 'string' },
                        { field: 'openActions', template: GridOpenActionTemplate, headerText: t('warehouses.openActions'), width: '120', minWidth: '100', allowFiltering: false },
                        { field: 'isActive', type: 'boolean', template: cellTemplateActive, headerText: t('warehouses.active'), width: '100', minWidth: '100' },
                    ]}
                    moduleName={t('warehouses.warehouses')}
                    menuName={t('View')}
                >
                    <Inject services={[Page, Sort, Filter, Search, Toolbar, ExcelExport, Reorder, Resize, ColumnChooser, Group]} />
                </GridComponentStatefull>}
            </Card>
            {/* <Box>
            <TrashGridDialog trashType={TrashType.Warehouses} open={trashOpen} onClose={(e) => { setTrashOpen(false); if (e) refresh(); }} />
        </Box> */}
        </Box >
}
